import { useState } from 'react';
import { usePermissions } from '@/features/auth';
import type { GeneratedDocument } from '../types/agreementDocuments.types';
import type { DocumentsListStatus } from '../components/AgreementDocumentsTab/DocumentsListCard';
import { isListPanelFocusable } from '../utils/listPanelFocus';
import { useAgreementDocuments } from './useAgreementDocuments';
import { useGenerateDocumentForm } from './useGenerateDocumentForm';
import { useGeneratedDocumentFile } from './useGeneratedDocumentFile';

/** Documents are fetched only while the tab is open, and only for callers who may browse them. */
export function useAgreementDocumentsTab(id: string | undefined, isActive: boolean, agreementRef?: string) {
    const { hasPermission } = usePermissions();
    const canBrowse = hasPermission({ resource: 'generated_document', action: 'browse' });
    const canGenerate = hasPermission({ resource: 'generated_document', action: 'add' });
    const canRead = hasPermission({ resource: 'generated_document', action: 'read' });
    const documents = useAgreementDocuments(id, isActive && canBrowse);
    const [generateOpen, setGenerateOpen] = useState(false);
    const [detailsFor, setDetailsFor] = useState<GeneratedDocument | null>(null);
    const form = useGenerateDocumentForm(id, () => setGenerateOpen(false));
    const file = useGeneratedDocumentFile(id, agreementRef, isActive);
    const { query } = documents;

    const status: DocumentsListStatus = query.isError ? 'error' : query.data ? 'ready' : 'loading';

    return {
        canBrowse,
        canGenerate,
        canRead,
        status,
        page: query.data,
        currentPage: documents.page,
        now: query.dataUpdatedAt,
        onPageChange: documents.setPage,
        onRetry: () => void query.refetch(),
        onRefresh: () => void query.refetch(),
        generate: {
            isOpen: generateOpen,
            open: () => setGenerateOpen(true),
            close: () => {
                if (form.isSubmitting) return;
                form.discard();
                setGenerateOpen(false);
            },
            form,
        },
        file: {
            ...file,
            onView: canRead ? file.view : undefined,
            onDownload: canRead ? file.download : undefined,
        },
        details: { document: detailsFor, open: setDetailsFor, close: () => setDetailsFor(null) },
        focusablePanel: isListPanelFocusable({
            isLoading: query.isLoading,
            isError: query.isError,
            hasFocusableAction: canGenerate || (query.data?.data.length ?? 0) > 0,
            lastPage: query.data?.meta.lastPage,
        }),
    };
}
